import React, { useEffect, useRef, useState } from 'react'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import EditText from 'blocks.simple/editText/editText'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { Button } from 'molecules/Button'
import { palette } from 'theme'
import { addToolItems, canvasPresets, shapeIconByKind, shapeToolItems, zoomPresetValues } from '../templateEditor.data'
import { useTemplateEditorContext } from '../hooks/templateEditorContext'
import { CanvasPresetId, ShapeKind } from '../templateEditor.types'
import { formatZoomPercent } from '../templateEditor.utils'
import styles from './templateEditor__toolbar.jcss'

const addToolIconTypes: Record<typeof addToolItems[number]['id'], string> = {
    text: 'text',
    shape: 'shapeRect',
    list: 'listHorizontal',
    listH: 'listVertical',
    table: 'table',
}

const addToolIconSizes: Partial<Record<typeof addToolItems[number]['id'], number>> = {
    shape: 20,
}

export const TemplateEditorToolbar = () => {
    const {
        templateName,
        setTemplateName,
        presetId,
        canvasW,
        canvasH,
        zoom,
        isFitMode,
        setPreset,
        handleZoomSelect,
        placeMode,
        togglePlaceMode,
        enterPlaceMode,
        isPlacing,
        canUndo,
        canRedo,
        undo,
        redo,
        saveTemplate,
        createNewTemplate,
        isSaving,
        isDirty,
    } = useTemplateEditorContext()

    const [shapeMenuOpen, setShapeMenuOpen] = useState(false)
    const shapeMenuRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        if (!shapeMenuOpen) {
            return
        }

        const onPointerDown = (event: MouseEvent) => {
            if (shapeMenuRef.current && !shapeMenuRef.current.contains(event.target as Node)) {
                setShapeMenuOpen(false)
            }
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShapeMenuOpen(false)
            }
        }

        window.addEventListener('mousedown', onPointerDown)
        window.addEventListener('keydown', onKeyDown)
        return () => {
            window.removeEventListener('mousedown', onPointerDown)
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [shapeMenuOpen])

    const resolutionOptions = [
        ...canvasPresets.map((preset) => ({ id: preset.id, name: preset.label })),
        ...(presetId === 'custom' ? [{ id: 'custom', name: `${canvasW}×${canvasH}` }] : []),
    ]

    const zoomOptions = [
        { id: 'fit', name: translate('templateEditorFitWindow') },
        ...zoomPresetValues.map((value) => ({ id: String(value), name: formatZoomPercent(value) })),
        ...(!(zoomPresetValues as readonly number[]).includes(zoom)
            ? [{ id: 'custom', name: formatZoomPercent(zoom) }]
            : []),
    ]

    const zoomValue = isFitMode
        ? 'fit'
        : (zoomPresetValues as readonly number[]).includes(zoom)
        ? String(zoom)
        : 'custom'

    const activeShapeKind: ShapeKind = placeMode?.type === 'shape' ? placeMode.opts.shapeKind ?? 'rect' : 'rect'
    const shapeToolActive = placeMode?.type === 'shape' || shapeMenuOpen

    const selectShape = (kind: ShapeKind) => {
        enterPlaceMode('shape', { shapeKind: kind })
        setShapeMenuOpen(false)
    }

    return (
        <header className={cn(styles.toolbar, isPlacing && styles.toolbarPlacing)}>
            <div className={cn(styles.cluster, styles.clusterBrand)}>
                <EditText
                    className={styles.titleInput}
                    value={templateName}
                    onChange={setTemplateName}
                    rounded="full_mini"
                    indentSize="mini"
                    textSize="mini"
                />
            </div>

            <div className={styles.cluster}>
                <Dropdown
                    className={styles.select}
                    options={resolutionOptions}
                    value={presetId}
                    onChange={(option) => setPreset(option.id as CanvasPresetId)}
                />
            </div>

            <div className={cn(styles.cluster, styles.tools)}>
                {addToolItems.map(({ id, titleKey }) => {
                    if (id === 'shape') {
                        return (
                            <span
                                key={id}
                                className={styles.toolButtonWrap}
                                ref={shapeMenuRef}
                                title={translate(titleKey)}
                            >
                                <Button
                                    className={shapeToolActive ? styles.toolButtonActive : undefined}
                                    title={translate(titleKey)}
                                    icon={{
                                        type: shapeIconByKind[activeShapeKind],
                                        size: addToolIconSizes.shape ?? 18,
                                    }}
                                    onClick={() => setShapeMenuOpen((open) => !open)}
                                />
                                {shapeMenuOpen && (
                                    <div className={styles.shapeMenu} role="menu">
                                        {shapeToolItems.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={cn(
                                                    styles.shapeMenuItem,
                                                    placeMode?.type === 'shape' &&
                                                        (placeMode.opts.shapeKind ?? 'rect') === item.id &&
                                                        styles.shapeMenuItemActive
                                                )}
                                                onClick={() => selectShape(item.id)}
                                            >
                                                <Icon type={item.icon} size={28} />
                                                <span>{translate(item.titleKey)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </span>
                        )
                    }

                    return (
                        <span key={id} className={styles.toolButtonWrap} title={translate(titleKey)}>
                            <Button
                                className={placeMode?.type === id ? styles.toolButtonActive : undefined}
                                title={translate(titleKey)}
                                icon={{
                                    type: addToolIconTypes[id],
                                    size: addToolIconSizes[id] ?? 18,
                                }}
                                onClick={() => {
                                    setShapeMenuOpen(false)
                                    togglePlaceMode(id)
                                }}
                            />
                        </span>
                    )
                })}
            </div>

            <div className={styles.cluster}>
                <Icon
                    type="undo"
                    size={18}
                    containerClass={styles.toolbarIcon}
                    disabled={!canUndo}
                    onClick={canUndo ? () => undo() : undefined}
                    tooltip={{ html: translate('templateEditorUndo') }}
                />
                <Icon
                    type="redo"
                    size={18}
                    containerClass={styles.toolbarIcon}
                    disabled={!canRedo}
                    onClick={canRedo ? () => redo() : undefined}
                    tooltip={{ html: translate('templateEditorRedo') }}
                />
                <Dropdown
                    className={styles.zoomSelect}
                    options={zoomOptions}
                    value={zoomValue}
                    onChange={(option) => handleZoomSelect(option.id)}
                />
            </div>

            <div className={cn(styles.cluster, styles.clusterActions)}>
                <Button mod="default" size="s" disabled icon={{ type: 'download', size: 14, position: 'left' }}>
                    {translate('templateEditorExport')}
                </Button>
                <Button mod="default" size="s" disabled>
                    {translate('templateEditorFigma')}
                </Button>
                <Button
                    mod="default"
                    size="s"
                    icon={{ type: 'plus', size: 14, position: 'left' }}
                    onClick={() => createNewTemplate()}
                >
                    {translate('templateEditorNew')}
                </Button>
                <Button
                    mod="fillBlack"
                    size="s"
                    disabled={isSaving || !isDirty}
                    icon={{ type: 'diskette', size: 14, position: 'left', color: palette.white.main }}
                    onClick={() => saveTemplate()}
                >
                    {translate('save')}
                </Button>
            </div>
        </header>
    )
}
